// ─── ROLLING CONVERSATION SUMMARY ───────────────────────────────────────────
//
// A per-conversation narrative summary that updates every N=25 new messages.
// Loaded into the system prompt as a "STORY SO FAR" section so the AI has
// compressed continuity for older messages it no longer sees verbatim
// (after DM pagination loads only last ~60 and RP loads last 100).
//
// Different from character_arc_summary (which is cross-conversation,
// event-based, per-character+user). This is per-conversation, narrative —
// it captures emotional flow, scene beats, and current standing.

import { supabase, safeGetSession } from './supabaseClient.js';
import { AI_ABORT_MS } from './aiTimeouts.js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUMMARIZE_EVERY_N = 25; // refresh summary every 25 new messages
const RECENT_TAIL = 40;       // include last N messages in the summarization input

const SUMMARIZER_PROMPT = `You are a narrative-summary assistant for an adult fiction RP platform. Read a stretch of conversation between a player and a character and produce a TIGHT continuity summary that the AI can read at the top of future prompts.

The summary must answer:
1. Where things stand emotionally between them right now (one short paragraph)
2. The most important factual establishments — promises, revelations, declarations, physical events, shared decisions (bullet list, max 8)
3. Unresolved tension or open beats the next message should be aware of (bullet list, max 4)

Style:
- Concise. Each section is for the AI to read quickly. Total under 300 words.
- Use exact names, exact wording where it matters. No paraphrasing key promises or named events.
- Do NOT moralize, hedge, or explain what the user is "really" feeling. Stay descriptive.
- Markdown headers OK: ## EMOTIONAL STANDING / ## ESTABLISHED FACTS / ## UNRESOLVED THREADS

Output ONLY the summary text. No preamble, no metadata.`;

/**
 * Decide whether the conversation needs a fresh summary.
 *  - Trigger if no summary yet AND >= SUMMARIZE_EVERY_N total messages
 *  - Trigger if existing summary's msg_count is at least SUMMARIZE_EVERY_N behind
 */
/**
 * TRUE total message count for a thread, straight from the DB.
 *
 * Critical: callers used to pass `messages.length` — the CLIENT-LOADED page
 * (~60 rows) — as the current count. Once a stored rolling_summary_msg_count
 * grew past the page size, `loaded(60) - stored(702)` went negative and
 * shouldRefreshSummary() could NEVER be true again, so the summary froze
 * forever. Nicole's Ford DM had 6,150 messages still summarised as of msg 702.
 */
export async function getTrueMessageCount(type, id) {
  if (!id) return 0;
  const spec = type === 'story'
    ? { table: 'story_messages', col: 'story_id' }
    : type === 'channel'
      ? { table: 'channel_messages', col: 'channel_id' }
      : type === 'group'
        ? { table: 'group_chat_messages', col: 'group_id' }
        : { table: 'dm_messages', col: 'conversation_id' };
  try {
    const { count, error } = await supabase
      .from(spec.table)
      .select('id', { count: 'exact', head: true })
      .eq(spec.col, id);
    if (error) return 0;
    return count || 0;
  } catch {
    return 0;
  }
}

export function shouldRefreshSummary(currentMsgCount, lastSummaryAtCount) {
  if (!currentMsgCount || currentMsgCount < SUMMARIZE_EVERY_N) return false;
  const since = currentMsgCount - (lastSummaryAtCount || 0);
  return since >= SUMMARIZE_EVERY_N;
}

/**
 * Fetch the current rolling summary for a conversation.
 * @param {'dm'|'story'} type
 * @param {string} id - conversation/story id
 */
export async function getRollingSummary(type, id) {
  if (!id) return null;
  const table = type === 'story' ? 'stories' : type === 'channel' ? 'channels' : type === 'group' ? 'group_chats' : 'dm_conversations';
  try {
    const { data } = await supabase
      .from(table)
      .select('rolling_summary, rolling_summary_msg_count, rolling_summary_updated_at')
      .eq('id', id)
      .maybeSingle();
    return data || null;
  } catch (err) {
    console.warn('[rollingSummary] getRollingSummary error:', err);
    return null;
  }
}

/**
 * Format the rolling summary into a prompt section. Empty when no summary.
 */
export function formatSummaryForPrompt(summary, characterName, playerLabel) {
  if (!summary?.rolling_summary) return '';
  const last = summary.rolling_summary_updated_at
    ? ` (last updated ${new Date(summary.rolling_summary_updated_at).toISOString().slice(0, 10)})`
    : '';
  return `\n═══ STORY SO FAR — rolling summary of ${characterName} & ${playerLabel || 'the player'}'s relationship in this thread${last} ═══\nThis is a CANONICAL summary of older messages you can no longer see verbatim. Use as ground truth for emotional standing, established facts, and unresolved threads. It is a record of the PAST: any "recently", "tonight" or "three weeks" inside it was written as of its last-updated date above, not as of now, and nothing in it is happening at this moment.\n\n${summary.rolling_summary}\n`;
}

/**
 * Generate (or refresh) a rolling summary for a conversation.
 * Fire-and-forget — caller doesn't await.
 *
 * @param {object} params
 * @param {'dm'|'story'} params.type
 * @param {string} params.conversationId
 * @param {string} params.characterName
 * @param {string} params.playerLabel
 * @param {Array}  params.messages   - all currently-loaded messages (will use last RECENT_TAIL)
 * @param {number} params.totalCount - the actual total message count on this conversation
 * @param {string} [params.priorSummary] - existing summary text, if any (for incremental refresh)
 */
// ─── ONE SUMMARISATION AT A TIME, PER CONVERSATION (2026-09-23) ──────────────
// These refreshes are fire-and-forget and only write msg_count when they
// FINISH, so anything that calls them per message can stampede: a channel
// sitting at rolling_summary_msg_count 0 with thousands of messages satisfies
// shouldRefreshSummary on every single send, and Haley and Nicole send in
// bursts of five and ten. Before v368 the only caller was the AI-reply path,
// which was serialised by its own in-flight counter; now a human send and a
// draft call it too, so the guard belongs here — at the one place that knows
// a refresh is already running for this conversation.
const _summaryInFlight = new Set();

export async function refreshRollingSummary(params) {
  const { type, conversationId, characterName, playerLabel, messages, totalCount, priorSummary } = params;
  if (!conversationId || !messages || messages.length === 0) return;
  const inFlightKey = `${type}:${conversationId}`;
  if (_summaryInFlight.has(inFlightKey)) return;
  _summaryInFlight.add(inFlightKey);
  try {
    const { data: { session } = { session: null } } = await safeGetSession().catch(() => ({ data: { session: null } }));
    if (!session?.access_token) return;

    // Build the input: prior summary (if any) + recent tail of messages.
    const tail = messages.slice(-RECENT_TAIL).map(m => {
      // Normalize across DM and RP message shapes
      const isPlayer = m.sender_type === 'user' || m.isPlayer === true;
      const speaker = isPlayer ? (playerLabel || 'Player') : (m.name || characterName);
      const text = m.content || m.text || '';
      return `${speaker}: ${String(text).slice(0, 600)}`;
    }).join('\n');

    const input = priorSummary
      ? `PRIOR SUMMARY (refresh and extend):\n${priorSummary}\n\n${'-'.repeat(40)}\n\nNEW MESSAGES SINCE PRIOR SUMMARY:\n${tail}`
      : `RECENT MESSAGES (no prior summary exists — produce a fresh summary):\n${tail}`;

    const res = await fetch(`${SUPABASE_URL}/functions/v1/ai-proxy`, {
      method: 'POST',
      // AI_ABORT_MS guard (audit P2-14; see aiTimeouts.js): a hung background call used to pin its promise across iOS suspend/resume forever.
      signal: AbortSignal.timeout(AI_ABORT_MS),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        provider: 'claude-haiku',
        nsfw: true,
        systemPrompt: SUMMARIZER_PROMPT,
        messages: [{ role: 'user', content: input }],
        // 2500, not 600 (2026-09-02): the players' DeepSeek V4 models spend
        // hidden reasoning tokens out of this same budget, so 600 returned NO
        // visible text (finish_reason=length) on 9 of 33 rolling summaries in
        // one week — refused by the truncation guard, so the channel's memory
        // silently stopped advancing. The summary itself is ~300-600 tokens;
        // the rest is headroom for the model to think first.
        maxTokens: 2500,
        feature: 'rolling_summary',
        characterId: null,
        characterName,
      }),
    });
    if (!res.ok) return;
    const data = await res.json();
    const summary = (data.text || '').trim();
    if (!summary) return;

    const table = type === 'story' ? 'stories' : type === 'channel' ? 'channels' : type === 'group' ? 'group_chats' : 'dm_conversations';
    await supabase
      .from(table)
      .update({
        rolling_summary: summary,
        rolling_summary_msg_count: totalCount,
        rolling_summary_updated_at: new Date().toISOString(),
      })
      .eq('id', conversationId);
  } catch (err) {
    console.warn('[rollingSummary] refresh failed (non-fatal):', err);
  } finally {
    _summaryInFlight.delete(inFlightKey);
  }
}
